import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  DOCUMENT_MIMES,
  EXECUTABLE_MIMES,
  IMAGE_FORMATS,
} from '../config/variables';
import { getBasename } from '../lib/File';
import { isAudio, isVideo } from '../lib/Media';
import { ProgressCallback, UploadContext } from './Content/CreateParams';
import { UploadableType } from '../enums/UploadableType';
import UploadParametersInterface, {
  UploadParametersVars,
} from '../graphql/Uploads/UploadParametersInterface';

interface UploadResult {
  uri: string;
}

export interface UploadAttachmentResult extends UploadResult {
  filename: string;
  mime: string;
}

export interface UploadAudioResult extends UploadResult {
  totalSeconds: number;
}

export interface UploadDocumentResult extends UploadResult {
  filename: string;
  mime: string;
}

export type UploadImageResult = UploadResult;

export interface UploadVideoResult extends UploadResult {
  totalSeconds: number;
}

class CloudStorage {
  public static async getUploadParameters(
    variables: UploadParametersVars,
    contactId?: string
  ) {
    const uploadParameters = await new UploadParametersInterface(
      contactId
    ).getUploadParameters(variables);

    return uploadParameters;
  }

  public static uploadFile(
    url: string,
    file: File,
    onUploadProgress?: ProgressCallback,
    contentType?: string
  ) {
    return axios.put(url, file, {
      onUploadProgress,
      headers: contentType ? { 'Content-Type': contentType } : undefined,
    });
  }

  public static uploadAttachment(
    context: UploadContext,
    file: File,
    path: string,
    progressCallback?: ProgressCallback
  ) {
    return new Promise<UploadAttachmentResult>((resolve, reject) => {
      if (EXECUTABLE_MIMES.includes(file.type)) {
        reject('Invalid attachment format');
      } else {
        const filename = getBasename(file);

        (async () => {
          try {
            const contentType = file.type || undefined;
            // Retrieve upload parameters (signed url)
            const key = `protected/organization-${context.organizationId}/${path}`;
            const uploadParameters = await CloudStorage.getUploadParameters(
              { type: UploadableType.ATTACHMENT, key, filename, contentType },
              context.contactId
            );
            // Upload file directly to S3 using the provided signed url
            await CloudStorage.uploadFile(
              uploadParameters.url,
              file,
              progressCallback,
              contentType
            );
            // File successfully uploaded
            resolve({
              filename,
              mime: file.type,
              // Must append leading slash on save to match DB expected path
              uri: `/${key}`,
            } as UploadAttachmentResult);
          } catch (e) {
            reject('Error uploading attachment');
          }
        })();
      }
    });
  }

  public static uploadAudio(
    context: UploadContext,
    file: File,
    progressCallback: ProgressCallback
  ) {
    return new Promise<UploadAudioResult>((resolve, reject) => {
      // Matches audio/wav and audio/webm
      if (!isAudio(file.type)) {
        reject('Invalid audio format');
      } else {
        // Create a virtual DOM object to extract the audio metadata
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        // Saving flag is used to avoid multiple save flows
        let isSaving = false;
        // Check the attached audio listeners
        const handler = async () => {
          if (isSaving) return;
          const { duration } = audio;
          if (duration === Infinity) {
            // Hack! It only happens when the audio is recorded (not file upload)
            // There is known bug where Chrome reports Infinity duration
            // unless the time is set to any high value. Let the "loadedmetadata"
            // fail, then the duration is read on "timeupdate"
            // @see https://bugs.chromium.org/p/chromium/issues/detail?id=642012
            //      https://bugs.chromium.org/p/chromium/issues/detail?id=916563
            audio.currentTime = Number.MAX_SAFE_INTEGER;
            return;
          }
          isSaving = true;
          try {
            const filename = getBasename(file, true);
            const path = `audio-${Date.now()}-${uuidv4()}/${filename}`;
            const contentType = file.type || undefined;
            // Retrieve upload parameters (signed url)
            const key = `protected/organization-${context.organizationId}/${path}`;
            const uploadParameters = await CloudStorage.getUploadParameters(
              { type: UploadableType.AUDIO, key, contentType },
              context.contactId
            );
            // Upload file directly to S3 using the provided signed url
            await CloudStorage.uploadFile(
              uploadParameters.url,
              file,
              progressCallback,
              contentType
            );
            // File successfully uploaded
            isSaving = false;
            resolve({
              totalSeconds: duration,
              // Must append leading slash on save to match DB expected path
              uri: `/${key}`,
            } as UploadAudioResult);
          } catch (e) {
            reject('Error uploading audio');
          }
        };
        audio.addEventListener('loadedmetadata', handler);
        audio.addEventListener('timeupdate', handler);
        audio.onerror = () => {
          reject('Invalid audio');
        };
        audio.src = URL.createObjectURL(file);
      }
    });
  }

  public static uploadDocument(
    context: UploadContext,
    file: File,
    path: string,
    progressCallback: ProgressCallback,
    strictFilename = false,
    type: UploadableType = UploadableType.DOCUMENT
  ) {
    return new Promise<UploadDocumentResult>((resolve, reject) => {
      if (!DOCUMENT_MIMES.includes(file.type)) {
        reject('Invalid document format');
      } else {
        const filename = getBasename(file, strictFilename);
        (async () => {
          try {
            const contentType = file.type || undefined;
            // Retrieve upload parameters (signed url)
            const key = `protected/organization-${context.organizationId}/${path}`;
            const uploadParameters = await CloudStorage.getUploadParameters(
              { type, key, filename, contentType },
              context.contactId
            );
            // Upload file directly to S3 using the provided signed url
            await CloudStorage.uploadFile(
              uploadParameters.url,
              file,
              progressCallback,
              contentType
            );
            // File successfully uploaded
            resolve({
              filename,
              mime: file.type,
              // Must append leading slash on save to match DB expected path
              uri: `/${key}`,
            } as UploadDocumentResult);
          } catch (e) {
            reject('Error uploading document');
          }
        })();
      }
    });
  }

  public static uploadImage(
    context: UploadContext,
    file: File,
    path: string,
    progressCallback?: ProgressCallback,
    supportedFormats = IMAGE_FORMATS
  ) {
    return new Promise<UploadResult>((resolve, reject) => {
      if (!supportedFormats.includes(file.type)) {
        reject('Invalid image format');
      } else {
        (async () => {
          try {
            const contentType = file.type || undefined;
            // Retrieve upload parameters (signed url)
            const key = `protected/organization-${context.organizationId}/${path}`;
            const uploadParameters = await CloudStorage.getUploadParameters(
              { type: UploadableType.IMAGE, key, contentType },
              context.contactId
            );
            // Upload file directly to S3 using the provided signed url
            await CloudStorage.uploadFile(
              uploadParameters.url,
              file,
              progressCallback,
              contentType
            );
            // File successfully uploaded
            resolve({
              // Must append leading slash on save to match DB expected path
              uri: `/${key}`,
            } as UploadResult);
          } catch (e) {
            reject('Error uploading image');
          }
        })();
      }
    });
  }

  public static uploadVideo(
    context: UploadContext,
    file: File,
    progressCallback: ProgressCallback
  ) {
    return new Promise<UploadVideoResult>((resolve, reject) => {
      // Matches video/mp4 and video/webm
      if (!isVideo(file.type)) {
        reject('Invalid video format');
      } else {
        const filename = getBasename(file, true);

        // Create a virtual DOM object to extract the video metadata
        const video = document.createElement('video');
        video.preload = 'metadata';
        // Saving flag is used to avoid multiple save flows
        let isSaving = false;
        // Check the attached video listeners
        const handler = async () => {
          if (isSaving) return;
          const { duration } = video;
          if (duration === Infinity) {
            // Hack! It only happens when the video is recorded (not file upload)
            // There is known bug where Chrome reports Infinity duration
            // unless the time is set to any high value. Let the "loadedmetadata"
            // fail, then the duration is read on "timeupdate"
            // @see https://bugs.chromium.org/p/chromium/issues/detail?id=642012
            //      https://bugs.chromium.org/p/chromium/issues/detail?id=916563
            video.currentTime = Number.MAX_SAFE_INTEGER;
            return;
          }
          isSaving = true;
          try {
            // Retrieve upload parameters (signed url)
            const path = `video-${Date.now()}-${uuidv4()}/${filename}`;
            const key = `protected/organization-${context.organizationId}/${path}`;
            const contentType = file.type || undefined;

            const uploadParameters = await CloudStorage.getUploadParameters(
              { type: UploadableType.VIDEO, key, contentType },
              context.contactId
            );
            // Upload file directly to S3 using the provided signed url
            await CloudStorage.uploadFile(
              uploadParameters.url,
              file,
              progressCallback,
              contentType
            );
            // File successfully uploaded
            isSaving = false;
            resolve({
              totalSeconds: duration,
              // Must append leading slash on save to match DB expected path
              uri: `/${key}`,
            });
          } catch (e) {
            reject('Error uploading video');
          }
        };
        video.addEventListener('loadedmetadata', handler);
        video.addEventListener('timeupdate', handler);
        video.onerror = () => {
          reject('Invalid video');
        };
        video.src = URL.createObjectURL(file);
      }
    });
  }
}

export default CloudStorage;
